import { useCallback, useState } from "react";

import { Button, InfoBar, Menu } from "@shapr3d/frontend-lib";

import { zendeskNewTicketUrl } from "../../../config";
import useNavigation from "../../../hooks/navigation";
import { useMutateChargebeeAutoRenew } from "../../../hooks/useAccount";
import { useTeam } from "../../../hooks/useTeams";
import {
  type SeatBasedTeamSubscription,
  SeatBasedTeamSubscriptionOrigins,
} from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { Modal } from "../../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { SubscriptionTier, SubscriptionType } from "../../../types";
import CTARow from "../../CTARow/CTARow";
import { CTARowDivider } from "../../CTARowDivider/CTARowDivider";
import { GenericErrorModal } from "../../Modal/Modal";
import { AddSeatsModal } from "../AddSeatsModal/AddSeatsModal";
import { CancellationFlow } from "../CancellationFlow/CancellationFlow";
import { SubscriptionExpirationText } from "../SubscriptionExpirationText/SubscriptionExpirationText";
import {
  formatExpirationDate,
  getPlanDisplayName,
  getSourceDisplayName,
  getTierDisplayName,
} from "../subscriptionUtils";
import { TeamGracePeriodBanner } from "../TeamGracePeriodBanner/TeamGracePeriodBanner";
import { UpgradeToStudioModal } from "../UpgradeToStudioModal/UpgradeToStudioModal";

import style from "./SeatBasedSubscriptionInfo.module.css";

export const SeatBasedSubscriptionInfo: React.FC<{
  subscription: SeatBasedTeamSubscription;
  teamID: string;
}> = ({ subscription, teamID }) => {
  const [isCancellationFlowOpen, setIsCancellationFlowOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isAddSeatsModalOpen, setIsAddSeatsModalOpen] = useState(false);
  const [isCancelSeatsModalOpen, setIsCancelSeatsModalOpen] = useState(false);
  const [isCancelledModalOpen, setIsCancelledModalOpen] = useState(false);
  const [isGracePeriodModalOpen, setIsGracePeriodModalOpen] = useState(false);
  const [isUpgradeToStudioModalOpen, setIsUpgradeToStudioModalOpen] = useState(false);

  const subscriptionType =
    subscription.origin === SeatBasedTeamSubscriptionOrigins.Chargebee
      ? SubscriptionType.Chargebee
      : SubscriptionType.Team;

  const hasNonAutorenewingTeamSubscription = subscription.autoRenewal === false;
  const hasGracePeriodTeamSubscription = Boolean(subscription?.gracePeriodExpirationDate);

  const { t, m } = useTranslation();
  const { teamData } = useTeam(teamID);
  const tracker = useTracker();
  const navigation = useNavigation();

  const { mutate: mutateAutoRenew, isPending: isAutoRenewPending } = useMutateChargebeeAutoRenew({
    teamID,
  });

  const handleUpdatePaymentInfo = () => {
    tracker.trackButtonClick({
      action: ElementClickAction.UPDATE_PAYMENT_INFORMATION,
      location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
    });
    navigation.goToTeamBilling(teamID);
  };

  const handleAutoRenewChange = useCallback(
    (autoRenew: boolean) => {
      const teamSubscriptionID = subscription.id;
      mutateAutoRenew(
        {
          autoRenew,
          teamSubscriptionID,
        },
        {
          onError: () => setIsErrorModalOpen(true),
          onSettled: () => {
            setIsCancellationFlowOpen(false);
          },
        }
      );
    },
    [subscription, mutateAutoRenew]
  );

  const handleAddSeats = useCallback(() => {
    if (hasNonAutorenewingTeamSubscription) {
      setIsCancelledModalOpen(true);
      return;
    }

    if (hasGracePeriodTeamSubscription) {
      setIsGracePeriodModalOpen(true);
      return;
    }

    tracker.trackButtonClick({
      action: ElementClickAction.ADD_SEATS,
      location: ElementClickLocation.MANAGE_SEATS_MENU,
    });
    setIsAddSeatsModalOpen(true);
  }, [hasGracePeriodTeamSubscription, hasNonAutorenewingTeamSubscription, tracker]);

  return (
    <>
      <TeamGracePeriodBanner
        className={style.gracePeriodBanner}
        subscription={subscription}
        handleUpdatePaymentInfo={handleUpdatePaymentInfo}
      />
      <CTARow
        title={t(m.TeamSubscriptionPage.SubscriptionTitle)}
        text={getPlanDisplayName(
          subscriptionType,
          subscription.subscriptionPeriod,
          subscription.tier,
          teamData?.name,
          t
        )}
        CTA={
          subscription.tier === SubscriptionTier.Individual &&
          !subscription.gracePeriodExpirationDate &&
          subscription.autoRenewal !== false ? (
            <Button
              variant="borderless"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.UPGRADE_BUTTON,
                  location: ElementClickLocation.TEAM_SUBSCRIPTION_PAGE,
                });
                setIsUpgradeToStudioModalOpen(true);
              }}
            >
              {t(m.UpgradeToStudioModal.UpgradeButton)}
            </Button>
          ) : undefined
        }
      />
      <CTARowDivider />
      {subscription.tier !== SubscriptionTier.Individual && (
        <>
          <CTARow
            title={t(m.TeamSubscriptionPage.Seats)}
            text={subscription.editorSeats}
            CTA={
              <Menu
                activator={
                  <Button
                    variant="borderless"
                    onClick={() =>
                      tracker.trackButtonClick({
                        action: ElementClickAction.MANAGE_SEATS,
                      })
                    }
                  >
                    {t(m.TeamSubscriptionPage.Manage)}
                  </Button>
                }
              >
                <Menu.Item onClick={handleAddSeats} closeOnClick>
                  {t(m.TeamSubscriptionPage.AddSeats)}
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    setIsCancelSeatsModalOpen(true);
                    tracker.trackButtonClick({
                      action: ElementClickAction.CANCEL_SEATS,
                      location: ElementClickLocation.MANAGE_SEATS_MENU,
                    });
                  }}
                  closeOnClick
                >
                  {t(m.TeamSubscriptionPage.CancelSeat)}
                </Menu.Item>
              </Menu>
            }
          />
          <CTARowDivider />
        </>
      )}
      <CTARow
        title={
          <SubscriptionExpirationText
            autoRenews={subscription.autoRenewal}
            subscriptionType={subscriptionType}
            expirationDate={new Date(subscription.expirationDate)}
            subscriptionTier={subscription.tier}
          />
        }
        text={formatExpirationDate(subscription.expirationDate)}
        notification={
          !subscription.autoRenewal && (
            <InfoBar
              text={t(m.TeamSubscriptionPage.TurnOnAutoRenewalInfo, {
                tierName: getTierDisplayName(subscription.tier),
              })}
              variant="attention"
              cta={
                <Button
                  variant="grayscale"
                  onClick={() => {
                    handleAutoRenewChange(true);
                    tracker.trackButtonClick({ action: ElementClickAction.TURN_ON_AUTO_RENEWAL });
                  }}
                  disabled={isAutoRenewPending}
                >
                  {t(m.TeamSubscriptionPage.TurnOnAutoRenewal)}
                </Button>
              }
            />
          )
        }
        CTA={
          subscription.autoRenewal && (
            <Menu
              activator={
                <Button
                  variant="borderless"
                  style={{ height: "100%" }}
                  onClick={() => {
                    tracker.trackButtonClick({ action: ElementClickAction.MANAGE_AUTO_RENEWAL });
                  }}
                >
                  {t(m.TeamSubscriptionPage.Manage)}
                </Button>
              }
            >
              <Menu.Item
                onClick={() => {
                  setIsCancellationFlowOpen(true);
                  tracker.trackButtonClick({
                    action: ElementClickAction.CANCEL_SUBSCRIPTION,
                    location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
                  });
                }}
                disabled={isAutoRenewPending}
                closeOnClick
              >
                <div style={{ color: "var(--control-error-label)" }}>
                  {t(m.TeamBilling.CancelSubscription)}
                </div>
              </Menu.Item>

              <Menu.Item onClick={handleUpdatePaymentInfo} closeOnClick>
                {t(m.TeamSubscriptionPage.UpdatePaymentInfo)}
              </Menu.Item>
            </Menu>
          )
        }
      />
      <CTARowDivider />
      <CTARow
        title={t(m.TeamSubscriptionPage.PurchasedVia)}
        text={getSourceDisplayName(subscriptionType, t)}
      />
      <Modal
        title={t(m.TeamSubscriptionPage.AddSeatsTitle)}
        telemetryName="add_seat_error_no_auto_renew"
        size="small"
        open={isCancelledModalOpen}
        onClose={() => setIsCancelledModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <span>
            {t(m.TeamSubscriptionPage.AddingSeatsUnavailable, { teamName: teamData?.name })}
          </span>
          <div className={style.message}>
            <span>{t(m.TeamSubscriptionPage.SubscriptionCancelled)}</span>
            <span>{t(m.TeamSubscriptionPage.TurnOnAutoRenewalToAdd)}</span>
          </div>
        </div>
      </Modal>
      <Modal
        title={t(m.TeamSubscriptionPage.AddSeatsTitle)}
        telemetryName="add_seat_error_grace_period"
        size="small"
        open={isGracePeriodModalOpen}
        onClose={() => setIsGracePeriodModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <span>
            {t(m.TeamSubscriptionPage.AddingSeatsUnavailable, { teamName: teamData?.name })}
          </span>
          <div className={style.message}>
            <span>{t(m.TeamSubscriptionPage.GracePeriodMessage)}</span>
            <span>{t(m.TeamSubscriptionPage.UpdateCardToAdd)}</span>
          </div>
        </div>
      </Modal>
      <Modal
        title={t(m.TeamSubscriptionPage.CancelSeat)}
        telemetryName="seat_based_subscription_cancel_seats"
        size="small"
        open={isCancelSeatsModalOpen}
        onClose={() => setIsCancelSeatsModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <div className={style.message}>
            <span>{t(m.TeamSubscriptionPage.ContactSupportRemoveSeats)}</span>
          </div>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.GO_TO_CONTACT_SUPPORT,
                location: ElementClickLocation.CANCEL_SEATS_MODAL,
              });
              navigation.openUrlWithZendeskAuth(zendeskNewTicketUrl);
            }}
          >
            {t(m.TeamSubscriptionPage.ContactSupport)}
          </Button>
        </div>
      </Modal>
      <CancellationFlow
        open={isCancellationFlowOpen}
        onClose={() => setIsCancellationFlowOpen(false)}
        onCancelSubscription={() => handleAutoRenewChange(false)}
        isLoading={isAutoRenewPending}
        editorSeatCount={subscription.editorSeats}
        isSubscriptionInGracePeriod={Boolean(subscription.gracePeriodExpirationDate)}
        subscriptionExpirationDate={formatExpirationDate(subscription.expirationDate)}
        subscriptionTier={subscription.tier}
      />
      <AddSeatsModal
        subscription={subscription}
        teamID={teamID}
        open={isAddSeatsModalOpen}
        onClose={() => setIsAddSeatsModalOpen(false)}
      />
      <GenericErrorModal
        open={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        telemetryName="seat_based_subscription_chargebee_portal"
      />
      {isUpgradeToStudioModalOpen && (
        <UpgradeToStudioModal
          open
          onClose={() => setIsUpgradeToStudioModalOpen(false)}
          subscription={subscription}
          teamID={teamID}
        />
      )}
    </>
  );
};
